<template>
	<Switch.Root
		:checked="checked"
		:disabled="disabled"
		@checked-change="onChange"
		class="switch"
		:class="{ 'no-transition': !mounted }"
	>
		<Switch.Control
			class="slider"
			:class="{ active: checked, disabled: disabled }"
			:style="checked && !disabled && color ? { backgroundColor: color } : {}"
		>
			<Switch.Thumb class="thumb" />
		</Switch.Control>
		<Switch.HiddenInput />
	</Switch.Root>
</template>

<script setup lang="ts">
import { Switch } from "@ark-ui/vue"
import { nextTick, onMounted, ref } from "vue"

defineProps<{
	checked: boolean
	disabled?: boolean
	color?: string
}>()

const emit = defineEmits<{
	(e: "change", value: boolean): void
}>()

function onChange(details: { checked: boolean }) {
	emit("change", details.checked)
}

// avoids a slide/color transition playing on initial mount before the switch settles
const mounted = ref(false)
onMounted(() => nextTick(() => (mounted.value = true)))
</script>

<style scoped>
.switch {
	position: relative;
	display: inline-block;
	flex-shrink: 0;
	width: var(--width-toggle);
	height: var(--height-toggle);

	&.no-transition .slider,
	&.no-transition .thumb {
		transition: none;
	}
}

.slider {
	position: absolute;
	cursor: pointer;
	border-radius: var(--border-radius-full);
	inset: 0;
	background-color: var(--neutral-600);
	transition: background-color 0.2s ease;

	&[data-state="checked"] {
		background-color: var(--primary-green);
	}

	&:focus-visible {
		box-shadow: 0 0 1px var(--blue-dark);
	}

	&[data-disabled] {
		background-color: var(--neutral-200);
		cursor: not-allowed;
	}
}

.thumb {
	position: absolute;
	left: var(--spacing-xxs);
	bottom: var(--spacing-xxs);
	width: calc(var(--height-toggle) - (var(--spacing-xxs) * 2));
	height: calc(var(--height-toggle) - (var(--spacing-xxs) * 2));
	background-color: var(--neutral-0);
	border-radius: 50%;
	transition: transform 0.4s ease;

	.slider[data-state="checked"] & {
		transform: translateX(calc(var(--width-toggle) - var(--height-toggle)));
	}

	.slider[data-disabled] & {
		transition: none;
	}
}
</style>
